/**
 * Permission helpers - the single source of truth for "is this person staff?".
 *
 * There are TWO different notions of "admin" in RepCheck and conflating them is
 * a security bug, so read this before writing any privilege check:
 *
 *   user.role      WORKSPACE role: admin | manager | analyst | viewer.
 *                  Describes what someone can do INSIDE THEIR OWN workspace
 *                  (invite teammates, run scans). It DEFAULTS TO "admin" for
 *                  every account, so `role === 'admin'` is true for every
 *                  customer and confers no platform privilege whatsoever.
 *
 *   user.canAccess PLATFORM permissions, e.g. ["global-admin"]. Granted
 *                  deliberately, never by default. This is what gates RepCheck
 *                  staff surfaces, and it is enforced server-side by
 *                  requirePermission() reading the database - not the JWT - so
 *                  it cannot be forged by tampering with a token.
 *
 * Client-side checks decide what to RENDER. They are a UX affordance, never the
 * security boundary: the route guard and the API are.
 */

export const PERMISSIONS = {
  GLOBAL_ADMIN: "global-admin",
};

/** True only for RepCheck platform staff. */
export function isSuperAdmin(user) {
  return Array.isArray(user?.canAccess) && user.canAccess.includes(PERMISSIONS.GLOBAL_ADMIN);
}

/** True if the user holds a specific platform permission. */
export function hasPermission(user, permission) {
  return Array.isArray(user?.canAccess) && user.canAccess.includes(permission);
}

/**
 * Workspace-level capability: may this person manage their own workspace
 * (team, billing)? This is about the customer's OWN account, and has nothing
 * to do with platform staff access.
 */
export function canManageWorkspace(user) {
  return ["admin", "manager"].includes(user?.role);
}

/** Workspace owners and workspace admins can permanently delete client data. */
export function canDeleteClientData(user) {
  if (!user) return false;
  const id = user._id || user.id;
  return user.role === 'admin' || (!!id && (!user.teamOwner || String(id) === String(user.teamOwner)));
}
